/**
 * Auth Store
 * Zustand store for authentication state
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';

interface User {
    id: string;
    email: string;
    name: string;
    avatar?: string;
}

interface AuthState {
    user: User | null;
    token: string | null;
    isAuthenticated: boolean;
    login: (email: string, password: string) => Promise<void>;
    logout: () => void;
    setUser: (user: User) => void;
}

const API_URL = import.meta.env.VITE_API_URL || '';

export const useAuthStore = create<AuthState>()(
    persist(
        (set) => ({
            user: null,
            token: null,
            isAuthenticated: false,

            login: async (email: string, password: string) => {
                const response = await fetch(`${API_URL}/api/v1/auth/login`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ email, password }),
                });

                if (!response.ok) {
                    let errorMessage = 'Login failed';
                    try {
                        const error = await response.json();
                        errorMessage = error.error?.message || errorMessage;
                    } catch (e) {
                        errorMessage = `Server Error (${response.status}): The API might be offline.`;
                    }
                    throw new Error(errorMessage);
                }

                const result = await response.json();
                const { data } = result;
                set({
                    user: data.user,
                    token: data.token,
                    isAuthenticated: true,
                });
            },

            logout: () => {
                set({ user: null, token: null, isAuthenticated: false });
            },

            setUser: (user: User) => {
                set({ user });
            },
        }),
        {
            name: 'tpcms-auth',
            partialize: (state) => ({
                user: state.user,
                token: state.token,
                isAuthenticated: state.isAuthenticated,
            }),
        }
    )
);
